import type { ReactNode } from "react";
import { useCallback, useState } from "react";

import dayjs from "dayjs";

import type {
    AnnotationHeaderRendererCallback,
    IAnnotation,
    AnnotationCreateStartCallback,
    AnnotationRemoveStartCallback,
} from "@similarweb/pro-logic-components";
import { AnnotationProvider, shortMonthDayAndYear, serverResponseFormat } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { useAnnotationsMutations } from "./useAnnotationsMutations";
import { useAnnotationsQuery } from "./useAnnotationsQuery";

const DEFAULT_TAG = "default";

export interface IAnnotationsContextProviderProps {
    children: ReactNode;
    chartName: string;
    annotationId: string;
    tagPrefix: string;
    fetchTagPrefixes?: string[];
    enabled?: boolean;
    granularity?: GranularityNames;
}

export const AnnotationsContextProvider = ({
    children,
    chartName,
    enabled = true,
    annotationId,
    tagPrefix,
    fetchTagPrefixes = [tagPrefix],
    granularity = GranularityNames.DAILY,
}: IAnnotationsContextProviderProps) => {
    const [formText, setFormText] = useState<string>("");
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const { createAnnotation, deleteAnnotation } = useAnnotationsMutations();
    const creationTag = `${tagPrefix}:${annotationId}`;
    const fetchTags = fetchTagPrefixes.map((prefix) => `${prefix}:${annotationId}`);
    const tags = [DEFAULT_TAG, ...fetchTags];
    const [deletingAnnotationId, setDeletingAnnotationId] = useState<IAnnotation["id"] | null>(null);
    const { data = [], isLoading: isQueryLoading } = useAnnotationsQuery(tags, { enabled });

    const onMutationStart = useCallback(
        ({ text }: { text: string }): void => {
            allTrackers.dispatchTrackEvent("Annotation", "Annotation", `Annotation Saved/${chartName}`, {});

            setIsCreating(true);
            setFormText(text);
        },
        [chartName],
    );

    const onMutationSuccess = useCallback((): void => {
        setIsCreating(false);
        setFormText("");
    }, []);

    const handleAnnotationCreated = useCallback(
        (pointX: number, text: string): void => {
            const key = dayjs(pointX).format(serverResponseFormat);

            onMutationStart({ text });
            createAnnotation.mutate(
                [
                    {
                        key,
                        text,
                        tags: [creationTag],
                        userId: null,
                    },
                ],
                {
                    onSuccess: () => {
                        onMutationSuccess();
                    },
                    onError: (error) => {
                        Sentry.errorTracker({
                            e: error,
                            message: "Error creating annotation",
                            level: "error",
                            params: {
                                extra: {
                                    key,
                                    text,
                                    tags: [creationTag],
                                },
                            },
                        });
                    },
                    onSettled: () => {
                        if (!isQueryLoading) {
                            setIsCreating(false);
                            setFormText("");
                        }
                    },
                },
            );
        },
        [createAnnotation, creationTag, isQueryLoading, onMutationStart, onMutationSuccess],
    );

    const handleAnnotationDeleted = useCallback(
        (annotation: IAnnotation): void => {
            allTrackers.dispatchTrackEvent(
                "Annotation",
                "Annotation Deletion confirmed",
                `Annotation Deletion confirmed/${chartName}`,
            );

            setDeletingAnnotationId(annotation.id);
            return deleteAnnotation.mutate([Number(annotation.id)], {
                onSettled: () => {
                    if (!isQueryLoading) {
                        setDeletingAnnotationId(null);
                    }
                },
                onError: (error) => {
                    Sentry.errorTracker({
                        e: error,
                        message: "Error deleting annotation",
                        level: "error",
                        params: {
                            extra: {
                                id: annotation.id,
                                type: annotation.type,
                            },
                        },
                    });
                },
            });
        },
        [chartName, deleteAnnotation, isQueryLoading],
    );

    const defaultHeaderRendererCallback: AnnotationHeaderRendererCallback = useCallback(
        ({ pointX, nextPointX, isCreating: isCallbackCreating }): string => {
            if (granularity === GranularityNames.DAILY) {
                const formatted = dayjs(pointX).format(shortMonthDayAndYear);
                return isCallbackCreating
                    ? translate("chart.annotations.add.panel.header", {
                          date: formatted,
                      })
                    : `${formatted}`;
            }
            const current = dayjs(pointX).format(shortMonthDayAndYear);
            const next = dayjs(nextPointX).format(shortMonthDayAndYear);
            return isCallbackCreating
                ? translate("chart.annotations.add.panel.header", {
                      date: current,
                  })
                : `${current} - ${next}`;
        },
        [granularity],
    );

    const handleAnnotationCreateStarted: AnnotationCreateStartCallback = useCallback(
        (_point, isFirst): void => {
            allTrackers.dispatchTrackEvent(
                "Annotation",
                "Add Annotation Clicked",
                `Add Annotation Clicked/${isFirst ? "Chart" : "Annotation widget"}/${chartName}`,
            );
        },
        [chartName],
    );

    const handleAnnotationDeleteStarted: AnnotationRemoveStartCallback = useCallback((): void => {
        allTrackers.dispatchTrackEvent(
            "Annotation",
            "Delete Annotation Clicked",
            `Delete Annotation Clicked/${chartName}`,
        );
    }, [chartName]);

    return enabled ? (
        <AnnotationProvider
            annotations={data}
            formText={formText}
            onAnnotationCreated={handleAnnotationCreated}
            onAnnotationRemoved={handleAnnotationDeleted}
            onAnnotationCreateStarted={handleAnnotationCreateStarted}
            onAnnotationRemoveStarted={handleAnnotationDeleteStarted}
            annotationHeaderLabelRenderer={defaultHeaderRendererCallback}
            deletingId={deletingAnnotationId}
            isCreating={isCreating}
        >
            {children}
        </AnnotationProvider>
    ) : (
        children
    );
};
